import React, { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getProyectosKpisData,
  getProyectosEvolucionData,
  getProyectosMediaEvolucionData,
  getProyectosPorTipoData,
  getProyectosPorPaisData,
  getProyectosTareasData,
  getProyectosParcialData,
  getProyectosRendimientoData,
} from "../../slices/dashboardProyectos/thunk";

import { I } from "../../Components/CMI/icons";
import { KPI, CompactKPI, ChartCard, Badge, Empty, Legend } from "../../Components/CMI/primitives";
import { GroupedBarChart, DonutChart, DonutLegend, Funnel, HorizontalBarList, fmtEur, fmtN, fmtPct } from "../../Components/CMI/charts";
import PageHead, { SectionHead, Footer } from "../../Layouts/CMI/PageHead";
import InformeMayo2026 from "./InformeMayo2026";
import { MESES, mesesHastaActual, rangoMes } from "../../helpers/meses";

const MONTHS = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const MESES_OPCIONES = mesesHastaActual();
const SEGMENTOS = [
  { id: "todos",      label: "Todos" },
  { id: "empresa",    label: "Empresa" },
  { id: "particular", label: "Particular" },
];

const deltaPill = (pct) => {
  if (pct == null || isNaN(pct)) return null;
  const pos = pct >= 0;
  return { text: `${pos ? "↑" : "↓"} ${Math.abs(pct).toFixed(1).replace(".", ",")}%`, tone: pos ? "pos" : "neg" };
};

const empresaLabel = (id) => {
  if (id === "all") return "Todas";
  if (id === 13) return "Visados";
  if (id === 12) return "Permiso";
  if (id === 14) return "CVS";
  if (id === "visados_cvs") return "Visados+CVS";
  return String(id);
};

const DashboardProyectos = () => {
  document.title = "Operativa | CMI - Grupo Cucalón Estévez";
  const dispatch = useDispatch();
  const { empresaId, year, vista } = useSelector((s) => s.Filters);
  const { kpis, evolucion, mediaEvolucion, porTipo, porPais, tareas, parcial, rendimiento } = useSelector((s) => s.DashboardProyectos);
  const [mes, setMes] = useState(new Date().getMonth());
  const [segmento, setSegmento] = useState("todos");
  const [taskTab, setTaskTab] = useState("Vencidas");
  const [showInforme, setShowInforme] = useState(false);
  const esPermiso = Number(empresaId) === 12;

  const loadAll = useCallback(() => {
    dispatch(getProyectosKpisData());
    dispatch(getProyectosEvolucionData());
    dispatch(getProyectosMediaEvolucionData());
    dispatch(getProyectosPorTipoData());
    dispatch(getProyectosPorPaisData());
    dispatch(getProyectosTareasData());
  }, [dispatch]);

  const loadParcial = useCallback(() => {
    const filtro = rangoMes(new Date().getFullYear(), mes);
    dispatch(getProyectosParcialData({ filtro, segmento }));
    dispatch(getProyectosRendimientoData({ filtro, segmento }));
  }, [dispatch, mes, segmento]);

  useEffect(() => { loadAll(); }, [loadAll, empresaId, year]);
  useEffect(() => { if (vista === "mensual") loadParcial(); }, [loadParcial, empresaId, vista]);
  useEffect(() => {
    const h = () => { loadAll(); loadParcial(); };
    window.addEventListener("cmi:refresh", h);
    return () => window.removeEventListener("cmi:refresh", h);
  }, [loadAll, loadParcial]);

  // KPIs
  const activos = kpis?.activos ?? 0;
  const nuevos = kpis?.nuevos_ano?.total ?? 0;
  const nuevosDelta = deltaPill(kpis?.nuevos_ano?.variacion_vs_ano_anterior);
  const aceptados = kpis?.aceptados_ano?.total ?? 0;
  const aceptadosDelta = deltaPill(kpis?.aceptados_ano?.variacion_vs_ano_anterior);
  const tasaAcept = kpis?.tasa_aceptacion ?? null;
  const pendNum = kpis?.pendientes_facturacion_cobro?.num ?? 0;
  const pendValor = kpis?.pendientes_facturacion_cobro?.valor ?? 0;
  const estadosCount = (kpis?.por_estado || []).length;

  // Evolución mensual
  const meses = evolucion?.meses || [];
  const creadosCur = MONTHS.map((_, i) => meses[i]?.total ?? 0);
  const creadosPrev = MONTHS.map((_, i) => meses[i]?.total_ano_anterior ?? 0);
  const aceptadosCur = MONTHS.map((_, i) => meses[i]?.aceptados ?? 0);

  // Por tipo
  const tipoColors = [1,3,6,4,2,7,5,8,10,9].map(n => `var(--ce-chart-${n})`);
  const tiposArr = (porTipo?.tipos || []).map((t, i) => ({
    label: t.tipo,
    value: t.total,
    color: tipoColors[i % tipoColors.length],
  }));

  // Por país
  const paises = porPais?.paises || [];

  // Tareas
  const vencidasNum = tareas?.vencidas?.num ?? 0;
  const proximasNum = tareas?.proximas?.num ?? 0;
  const tareasVencidas = tareas?.vencidas?.tareas || [];
  const tareasProximas = tareas?.proximas?.tareas || [];
  const tareasMostradas = taskTab === "Vencidas" ? tareasVencidas : tareasProximas;

  // Media evolución por estado
  const estados = mediaEvolucion?.estados || [];

  // Parciales
  const p = parcial?.kpis || {};
  const parcialYear = parcial?.year || new Date().getFullYear();
  const estadosParcial = parcial?.media_evolucion?.estados || [];
  const totalProyParcial = parcial?.media_evolucion?.total_proyectos ?? 0;

  // Rendimiento operativo (ranking, embudo, tareas/acciones, aging)
  const ranking = rendimiento?.ranking || [];
  const embudo = rendimiento?.embudo || [];
  const tareasOp = rendimiento?.tareas || null;
  const aging = rendimiento?.aging || null;
  const agingColors = ["var(--ce-chart-3)", "var(--ce-chart-4)", "var(--ce-danger)"];
  const periodLabel = MESES[mes];

  // Segmento empresa/particular
  const segLabel = (SEGMENTOS.find((s) => s.id === segmento) || {}).label || "Todos";
  const segSuffix = segmento === "todos" ? "" : ` · ${segLabel.toLowerCase()}`;

  // Motivos de rechazo (proyectos desestimados del periodo)
  const motivos = rendimiento?.motivos || null;
  const motivoColors = [4, 5, 7, 8, 10, 2, 1, 3].map((n) => `var(--ce-chart-${n})`);
  const motivosArr = (motivos?.items || []).map((m, i) => ({
    label: m.motivo,
    value: m.total,
    color: motivoColors[i % motivoColors.length],
  }));

  // Conversión por tipo de servicio
  const servicios = rendimiento?.servicios || [];

  // Actividad por fuente (SDR/CRM vs Comercial/ERP) y seguimientos automáticos
  const actividad = rendimiento?.actividad || null;
  const pctAuto = tareasOp?.pct_auto ?? 0;

  return (
    <>
      <PageHead
        icon={I.Briefcase}
        title="Dashboard Operativa"
        sub={vista === "anual"
          ? `Datos anuales · empresa ${empresaLabel(empresaId)} · año ${year}`
          : `Datos mensuales · empresa ${empresaLabel(empresaId)} · ${MESES[mes]}`}
        screenId="proyectos"
      />

      {esPermiso ? (
        <div style={{ marginBottom: 18 }}>
          <button className="btn" onClick={() => setShowInforme(true)}>
            <I.FileText size={16} />
            Informe proyectos Mayo 2026
          </button>
        </div>
      ) : null}

      {showInforme ? <InformeMayo2026 onClose={() => setShowInforme(false)} /> : null}

      {vista === "anual" && (
      <>
      <div className="grid gc-3">
        <KPI tier="hero" tone="primary" icon={I.Briefcase}
          label="Proyectos Activos"
          value={fmtN(activos)}
          info={{
            descripcion: "Proyectos abiertos en este momento (excluye desestimados, finalizados y archivados).",
            origen: "BD erpvisados_2023 — tabla proyectos.",
            formula: "COUNT(*) WHERE estadosdeproyecto_id NOT IN (5, 6, 9) AND eliminado = 0.",
          }}
        />
        <KPI tier="primary" tone="cyan" icon={I.Plus}
          label="Nuevos Este Año"
          value={fmtN(nuevos)}
          delta={nuevosDelta?.text} deltaTone={nuevosDelta?.tone} deltaRef="vs año anterior"
          info={{
            descripcion: "Proyectos dados de alta en el año seleccionado, con la variación frente al año anterior.",
            origen: "BD erpvisados_2023 — tabla proyectos.",
            formula: "COUNT(*) WHERE YEAR(fecha_alta) = año AND eliminado = 0.",
          }}
        />
        <KPI tier="primary" tone="warning" icon={I.FileText}
          label="Pendientes Facturación"
          value={fmtN(pendNum)}
          extras={<span className="kpi-ref">{fmtEur(pendValor)} estimado</span>}
          info={{
            descripcion: "Proyectos en estados de pendiente de facturación o pendiente de cobro, con el valor agregado de sus líneas.",
            origen: "BD erpvisados_2023 — proyectos LEFT JOIN proyectolineas.",
            formula: "COUNT(*) y Σ (importe × Cantidad) WHERE estadosdeproyecto_id IN (7, 8, 11).",
          }}
        />
      </div>

      <div className="grid gc-3" style={{ marginTop: 20 }}>
        <KPI tier="secondary" tone="success" icon={I.CheckCheck}
          label="Aceptados Este Año"
          value={fmtN(aceptados)}
          delta={aceptadosDelta?.text} deltaTone={aceptadosDelta?.tone} deltaRef="vs año anterior"
          info={{
            descripcion: "Proyectos cuya fecha de aceptación cae en el año seleccionado, con la variación interanual.",
            origen: "BD erpvisados_2023 — tabla proyectos.",
            formula: "COUNT(*) WHERE YEAR(FechaProyectoAceptado) = año AND estadosdeproyecto_id NOT IN (1, 5).",
          }}
        />
        <KPI tier="secondary" tone="primary" icon={I.Percent}
          label="Tasa de Aceptación"
          value={tasaAcept != null ? fmtPct(tasaAcept) : "—"}
          extras={<span className="kpi-ref">Aceptados / Creados este año</span>}
          info={{
            descripcion: "Porcentaje de proyectos creados en el año que han llegado a aceptarse.",
            origen: "Calculado en API a partir de proyectos creados y aceptados (BD).",
            formula: "Aceptados del año / Creados del año × 100.",
          }}
        />
        <KPI tier="secondary" tone="cyan" icon={I.BarChart3}
          label="Estados Activos"
          value={fmtN(estadosCount)}
          info={{
            descripcion: "Número de estados distintos en los que se distribuyen los proyectos no eliminados (todos los años).",
            origen: "BD erpvisados_2023 — proyectos LEFT JOIN estadosdeproyectos.",
            formula: "COUNT(DISTINCT estadosdeproyecto_id) sobre proyectos no eliminados de las empresas seleccionadas.",
          }}
        />
      </div>

      <div className="grid gc-8-4" style={{ marginTop: 20 }}>
        <ChartCard
          icon={I.BarChart3} title="Evolución Mensual de Proyectos"
          info={{
            titulo: "Evolución Mensual de Proyectos",
            descripcion: "Comparativa mensual de proyectos creados vs creados el año anterior, junto con los aceptados del año actual.",
            origen: "BD erpvisados_2023 — tabla proyectos (agrupada por mes de fecha_alta y FechaProyectoAceptado).",
            formula: "Creados = COUNT(*) GROUP BY MONTH(fecha_alta). Aceptados = COUNT(*) GROUP BY MONTH(FechaProyectoAceptado), excluyendo estados 1 y 5.",
          }}
          meta={<span>{fmtN(nuevos)} creados · {fmtN(aceptados)} aceptados</span>}
        >
          {meses.length ? (
            <GroupedBarChart
              months={MONTHS}
              series={[
                { label: `Creados ${year}`,   color: "var(--ce-chart-1)",        values: creadosCur },
                { label: `Creados ${year-1}`, color: "var(--ce-chart-previous)", values: creadosPrev, muted: true },
                { label: `Aceptados ${year}`, color: "var(--ce-chart-3)",        values: aceptadosCur },
              ]}
            />
          ) : (
            <Empty icon={I.BarChart3} title="Sin datos de evolución" />
          )}
        </ChartCard>

        <ChartCard icon={I.PieChart} title="Proyectos por Tipo"
          info={{
            titulo: "Proyectos por Tipo",
            descripcion: "Top 8 tipos de proyecto del año, con el porcentaje sobre el total.",
            origen: "BD erpvisados_2023 — proyectos LEFT JOIN proyectotipos.",
            formula: "COUNT(*) GROUP BY proyectotipo_id WHERE YEAR(fecha_alta) = año, ordenado por total descendente.",
          }}
        >
          {tiposArr.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14, alignItems: "center" }}>
              <DonutChart data={tiposArr} totalLabel="Total" totalValueFmt={fmtN} size={170} />
              <DonutLegend data={tiposArr} />
            </div>
          ) : (
            <Empty icon={I.PieChart} title="Sin tipos de proyecto" />
          )}
        </ChartCard>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1fr", marginTop: 20 }}>
        <ChartCard
          icon={I.Clock} title="Media de Evolución por Estado"
          info={{
            titulo: "Media de Evolución por Estado",
            descripcion: "Tiempo medio (en días) que tardan los proyectos del año en pasar de un estado al siguiente, y el tiempo acumulado desde la creación.",
            origen: "BD erpvisados_2023 — tabla proyectos (campos de fecha por estado).",
            formula: "Media = AVG(DATEDIFF(fecha_estado, fecha_estado_anterior)) y AVG(DATEDIFF(fecha_estado, fecha_alta)), filtrando casos en que las fechas son consistentes.",
          }}
          meta={mediaEvolucion?.total_proyectos ? <span>{fmtN(mediaEvolucion.total_proyectos)} proyectos analizados</span> : null}
        >
          {estados.length ? (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Campo de fecha</th>
                  <th className="right">Media desde estado anterior</th>
                  <th className="right">Media desde creación</th>
                  <th className="right">Proyectos</th>
                </tr>
              </thead>
              <tbody>
                {estados.map((r, i) => (
                  <tr key={i}>
                    <td>{r.descripcion}</td>
                    <td><span className="mono-pill">{r.campo_fecha}</span></td>
                    <td className="right num">{(r.media_dias_desde_estado_anterior ?? 0).toFixed(1).replace(".", ",")} días</td>
                    <td className="right num">{(r.media_dias_desde_alta ?? 0).toFixed(1).replace(".", ",")} días</td>
                    <td className="right num">{fmtN(r.proyectos_con_fecha ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty icon={I.Clock} title="Sin datos de evolución por estado" />
          )}
        </ChartCard>
      </div>

      <div className="grid gc-5-7" style={{ marginTop: 20 }}>
        <ChartCard icon={I.Globe} title="Top Países de Origen"
          info={{
            titulo: "Top Países de Origen",
            descripcion: "Top 12 países de origen de los proyectos creados en el año.",
            origen: "BD erpvisados_2023 — proyectos LEFT JOIN paises.",
            formula: "COUNT(*) GROUP BY pais_id WHERE YEAR(fecha_alta) = año, ordenado por total descendente.",
          }}
        >
          {paises.length ? (
            <table className="tbl">
              <thead><tr><th>País</th><th className="right">Proyectos</th></tr></thead>
              <tbody>
                {paises.slice(0, 10).map((p, i) => (
                  <tr key={i}>
                    <td>{p.pais}</td>
                    <td className="right num">{fmtN(p.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty icon={I.Globe} title="Sin datos de países" sub="Los proyectos no incluyen país de origen para esta empresa." />
          )}
        </ChartCard>

        <ChartCard
          icon={I.ListTodo} title="Tareas Pendientes"
          info={{
            titulo: "Tareas Pendientes",
            descripcion: "Tareas no realizadas: las ya vencidas (FechaFinal < hoy) y las próximas a vencer dentro de la ventana configurada (por defecto 7 días).",
            origen: "BD erpvisados_2023 — tareas LEFT JOIN usuarios LEFT JOIN proyectos.",
            formula: "Vencidas: Realizada = 0 AND FechaFinal < CURDATE(). Próximas: Realizada = 0 AND FechaFinal BETWEEN CURDATE() y CURDATE() + N días. Límite 50 por bloque.",
          }}
          meta={
            <div className="seg">
              <button className={taskTab === "Vencidas" ? "active" : ""} onClick={()=>setTaskTab("Vencidas")}>
                <span style={{color:"var(--ce-danger)", marginRight:4}}>●</span>{vencidasNum} Vencidas
              </button>
              <button className={taskTab === "Próximas" ? "active" : ""} onClick={()=>setTaskTab("Próximas")}>
                <span style={{color:"var(--ce-primary)", marginRight:4}}>●</span>{proximasNum} Próximas
              </button>
            </div>
          }
        >
          {tareasMostradas.length ? (
            <table className="tbl">
              <thead><tr><th>Descripción</th><th>Usuario</th><th className="right">Estado</th></tr></thead>
              <tbody>
                {tareasMostradas.slice(0, 10).map((t, i) => (
                  <tr key={i}>
                    <td>{t.descripcion || "—"}</td>
                    <td>{t.usuario || "—"}</td>
                    <td className="right">
                      {taskTab === "Vencidas"
                        ? <Badge tone="neg">Vencida {t.dias_vencida}d</Badge>
                        : <Badge tone="warn">{t.dias_restantes ?? "—"}d</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : taskTab === "Vencidas" ? (
            <Empty ok icon={I.Check} title="Sin tareas vencidas" sub="El equipo está al día con las tareas asignadas." />
          ) : (
            <Empty icon={I.Calendar} title="Sin tareas próximas" sub="Nada en la ventana de los próximos 7 días." />
          )}
        </ChartCard>
      </div>

      </>
      )}

      {vista === "mensual" && (
      <>
      <SectionHead
        icon={I.Calendar} title="Datos parciales"
        sub="Comparativa contra el mes anterior · el segmento se aplica también a Rendimiento y operativa"
        right={
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <div className="seg" role="tablist" aria-label="Segmento">
              {SEGMENTOS.map((sg) => (
                <button key={sg.id} className={segmento === sg.id ? "active" : ""} onClick={() => setSegmento(sg.id)}>{sg.label}</button>
              ))}
            </div>
            <select className="sel" value={mes} onChange={(e) => setMes(Number(e.target.value))} aria-label="Mes">
              {MESES_OPCIONES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        }
      />

      <SectionHead icon={I.Plus} title="1. Proyectos creados" small />
      <div className="grid gc-3">
        <KPI tier="primary" tone="primary" icon={I.FileText}
          label="Total"
          value={fmtN(p.creados?.total?.actual ?? 0)}
          delta={deltaPill(p.creados?.total?.variacion_pct)?.text}
          deltaTone={deltaPill(p.creados?.total?.variacion_pct)?.tone}
          extras={<span className="kpi-ref">Facturación asociada: {fmtEur(p.creados?.total?.facturacion ?? 0)}</span>}
        />
        <KPI tier="primary" tone="primary" icon={I.Briefcase}
          label="Cartera"
          value={fmtN(p.creados?.cartera?.actual ?? 0)}
          delta={deltaPill(p.creados?.cartera?.variacion_pct)?.text}
          deltaTone={deltaPill(p.creados?.cartera?.variacion_pct)?.tone}
          extras={<span className="kpi-ref">Facturación asociada: {fmtEur(p.creados?.cartera?.facturacion ?? 0)}</span>}
        />
        <KPI tier="primary" tone="primary" icon={I.Users}
          label="Clientes nuevos"
          value={fmtN(p.creados?.nuevos?.actual ?? 0)}
          delta={deltaPill(p.creados?.nuevos?.variacion_pct)?.text}
          deltaTone={deltaPill(p.creados?.nuevos?.variacion_pct)?.tone}
          extras={<span className="kpi-ref">Facturación asociada: {fmtEur(p.creados?.nuevos?.facturacion ?? 0)}</span>}
        />
      </div>

      <SectionHead icon={I.CheckCheck} title="2. Proyectos aceptados" small />
      <div className="grid gc-3">
        <KPI tier="primary" tone="success" icon={I.CheckCheck}
          label="Total"
          value={fmtN(p.aceptados?.total?.actual ?? 0)}
          delta={deltaPill(p.aceptados?.total?.variacion_pct)?.text}
          deltaTone={deltaPill(p.aceptados?.total?.variacion_pct)?.tone}
          extras={<span className="kpi-ref">Facturación asociada: {fmtEur(p.aceptados?.total?.facturacion ?? 0)}</span>}
        />
        <KPI tier="primary" tone="success" icon={I.Briefcase}
          label="Cartera"
          value={fmtN(p.aceptados?.cartera?.actual ?? 0)}
          delta={deltaPill(p.aceptados?.cartera?.variacion_pct)?.text}
          deltaTone={deltaPill(p.aceptados?.cartera?.variacion_pct)?.tone}
          extras={<span className="kpi-ref">Facturación asociada: {fmtEur(p.aceptados?.cartera?.facturacion ?? 0)}</span>}
        />
        <KPI tier="primary" tone="success" icon={I.Users}
          label="Clientes nuevos"
          value={fmtN(p.aceptados?.nuevos?.actual ?? 0)}
          delta={deltaPill(p.aceptados?.nuevos?.variacion_pct)?.text}
          deltaTone={deltaPill(p.aceptados?.nuevos?.variacion_pct)?.tone}
          extras={<span className="kpi-ref">Facturación asociada: {fmtEur(p.aceptados?.nuevos?.facturacion ?? 0)}</span>}
        />
      </div>

      <div className="grid gc-2" style={{ marginTop: 20 }}>
        <CompactKPI tone="danger" icon={I.X} label="Rechazados"
          value={fmtN(p.rechazados?.actual ?? 0)}
          prev={p.rechazados?.anterior != null ? `Anterior: ${fmtN(p.rechazados.anterior)}` : null}
          delta={deltaPill(p.rechazados?.variacion_pct)?.text}
          deltaTone={deltaPill(p.rechazados?.variacion_pct)?.tone}
        />
        <CompactKPI tone="warning" icon={I.Clock} label="Pendientes de aceptación"
          value={fmtN(p.pendientes_aceptacion?.actual ?? 0)}
          prev={p.pendientes_aceptacion?.anterior != null ? `Anterior: ${fmtN(p.pendientes_aceptacion.anterior)}` : null}
          delta={deltaPill(p.pendientes_aceptacion?.variacion_pct)?.text}
          deltaTone={deltaPill(p.pendientes_aceptacion?.variacion_pct)?.tone}
        />
      </div>

      <SectionHead icon={I.Percent} title="3. Tasa de aceptación" small />
      <div className="grid gc-3">
        <KPI tier="primary" tone="primary" icon={I.Percent}
          label="Total"
          value={fmtPct(p.tasa_aceptacion?.total?.actual ?? 0)}
          delta={deltaPill(p.tasa_aceptacion?.total?.variacion_pct)?.text}
          deltaTone={deltaPill(p.tasa_aceptacion?.total?.variacion_pct)?.tone}
          extras={<span className="kpi-ref">Media histórica {parcialYear}: {fmtPct(p.tasa_aceptacion?.total?.media_historica ?? 0)}</span>}
        />
        <KPI tier="primary" tone="primary" icon={I.Percent}
          label="Cartera"
          value={fmtPct(p.tasa_aceptacion?.cartera?.actual ?? 0)}
          delta={deltaPill(p.tasa_aceptacion?.cartera?.variacion_pct)?.text}
          deltaTone={deltaPill(p.tasa_aceptacion?.cartera?.variacion_pct)?.tone}
          extras={<span className="kpi-ref">Media histórica {parcialYear}: {fmtPct(p.tasa_aceptacion?.cartera?.media_historica ?? 0)}</span>}
        />
        <KPI tier="primary" tone="primary" icon={I.Percent}
          label="Clientes nuevos"
          value={fmtPct(p.tasa_aceptacion?.nuevos?.actual ?? 0)}
          delta={deltaPill(p.tasa_aceptacion?.nuevos?.variacion_pct)?.text}
          deltaTone={deltaPill(p.tasa_aceptacion?.nuevos?.variacion_pct)?.tone}
          extras={<span className="kpi-ref">Media histórica {parcialYear}: {fmtPct(p.tasa_aceptacion?.nuevos?.media_historica ?? 0)}</span>}
        />
      </div>

      <div className="grid gc-3" style={{ marginTop: 20 }}>
        <CompactKPI tone="cyan" icon={I.Briefcase} label="En ejecución"
          value={fmtN(p.en_ejecucion?.actual ?? 0)}
          prev={p.en_ejecucion?.anterior != null ? `Anterior: ${fmtN(p.en_ejecucion.anterior)}` : null}
          delta={deltaPill(p.en_ejecucion?.variacion_pct)?.text}
          deltaTone={deltaPill(p.en_ejecucion?.variacion_pct)?.tone}
        />
        <CompactKPI tone="warning" icon={I.Flag} label="Finalizados"
          value={fmtN(p.finalizados?.actual ?? 0)}
          prev={p.finalizados?.anterior != null ? `Anterior: ${fmtN(p.finalizados.anterior)}` : null}
          delta={deltaPill(p.finalizados?.variacion_pct)?.text}
          deltaTone={deltaPill(p.finalizados?.variacion_pct)?.tone}
        />
        <CompactKPI tone="warning" icon={I.FileText} label="Pendiente facturación"
          value={fmtEur(p.pendientes_valor?.actual ?? 0)}
          prev={p.pendientes_valor?.anterior != null ? `Anterior: ${fmtEur(p.pendientes_valor.anterior)}` : null}
          delta={deltaPill(p.pendientes_valor?.variacion_pct)?.text}
          deltaTone={deltaPill(p.pendientes_valor?.variacion_pct)?.tone}
        />
      </div>


      <div className="grid" style={{ gridTemplateColumns: "1fr", marginTop: 20 }}>
        <ChartCard
          icon={I.Clock} title="Media de Evolución por Estado (periodo)"
          info={{
            titulo: "Media de Evolución por Estado (periodo)",
            descripcion: "Tiempo medio (en días) que tardan los proyectos creados en el periodo seleccionado en pasar de un estado al siguiente, y el tiempo acumulado desde la creación.",
            origen: "BD erpvisados_2023 — tabla proyectos (campos de fecha por estado).",
            formula: "Media = AVG(DATEDIFF(fecha_estado, fecha_estado_anterior)) y AVG(DATEDIFF(fecha_estado, fecha_alta)) sobre proyectos con fecha_alta en el rango.",
          }}
          meta={totalProyParcial ? <span>{fmtN(totalProyParcial)} proyectos en el periodo</span> : null}
        >
          {estadosParcial.length ? (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Campo de fecha</th>
                  <th className="right">Media desde estado anterior</th>
                  <th className="right">Media desde creación</th>
                  <th className="right">Proyectos</th>
                </tr>
              </thead>
              <tbody>
                {estadosParcial.map((r, i) => (
                  <tr key={i}>
                    <td>{r.descripcion}</td>
                    <td><span className="mono-pill">{r.campo_fecha}</span></td>
                    <td className="right num">{(r.media_dias_desde_estado_anterior ?? 0).toFixed(1).replace(".", ",")} días</td>
                    <td className="right num">{(r.media_dias_desde_alta ?? 0).toFixed(1).replace(".", ",")} días</td>
                    <td className="right num">{fmtN(r.proyectos_con_fecha ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty icon={I.Clock} title="Sin datos en el periodo" />
          )}
        </ChartCard>
      </div>

      <div className="grid gc-5-7" style={{ marginTop: 20 }}>
        <ChartCard
          icon={I.PieChart} title="Motivos de rechazo"
          meta={motivos?.total ? <span>{fmtN(motivos.total)} desestimados{segSuffix}</span> : null}
          info={{
            titulo: "Motivos de rechazo",
            descripcion: "Distribución de los proyectos desestimados (presupuestos rechazados) del periodo según el motivo de descarte registrado en su lead de origen.",
            origen: "BD erpvisados_2023 — proyectos (estado 5) → leads → discardoptions.",
            formula: "COUNT(*) GROUP BY discardoptions.description, sobre proyectos con estadosdeproyecto_id = 5 y fecha_alta en el rango. Sin lead/motivo → «No especificado».",
          }}
        >
          {motivosArr.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14, alignItems: "center" }}>
              <DonutChart data={motivosArr} totalLabel="Rechazos" totalValueFmt={fmtN} size={170} />
              <DonutLegend data={motivosArr} />
            </div>
          ) : (
            <Empty ok icon={I.Check} title="Sin proyectos rechazados" sub="No hay presupuestos desestimados en el periodo y segmento seleccionados." />
          )}
        </ChartCard>

        <ChartCard
          icon={I.PieChart} title="Conversión por tipo de servicio"
          meta={servicios.length ? <span>{servicios.length} servicios{segSuffix}</span> : null}
          info={{
            titulo: "Conversión por tipo de servicio",
            descripcion: "Para cada tipo de servicio (proyectotipo), proyectos creados en el periodo, cuántos se aceptaron, cuántos se rechazaron y la tasa de aceptación. Permite ver qué servicios convierten mejor.",
            origen: "BD erpvisados_2023 — proyectos LEFT JOIN proyectotipos.",
            formula: "Por servicio: total = COUNT(*); aceptados = FechaProyectoAceptado NO NULA y estado NOT IN (1,5); rechazados = estado 5; tasa = aceptados / total × 100.",
          }}
        >
          {servicios.length ? (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Servicio</th>
                  <th className="right">Creados</th>
                  <th className="right">Aceptados</th>
                  <th className="right">Rechazados</th>
                  <th className="right">Tasa acept.</th>
                </tr>
              </thead>
              <tbody>
                {servicios.map((s, i) => (
                  <tr key={i}>
                    <td>{s.servicio}</td>
                    <td className="right num">{fmtN(s.total)}</td>
                    <td className="right num">{fmtN(s.aceptados)}</td>
                    <td className="right num">{s.rechazados > 0 ? <Badge tone="neg">{fmtN(s.rechazados)}</Badge> : "—"}</td>
                    <td className="right num">{fmtPct(s.tasa_aceptacion)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty icon={I.PieChart} title="Sin datos en el periodo" />
          )}
        </ChartCard>
      </div>

      <SectionHead
        icon={I.Users} title="Rendimiento y operativa"
        sub={`Evaluación de equipos y flujo de proyectos · ${(periodLabel || "periodo").toLowerCase()}${segSuffix}`}
      />

      <div className="grid gc-7-5">
        <ChartCard
          icon={I.Funnel} title="Embudo operativo"
          meta={embudo.length ? <span>{fmtN(embudo[0]?.value || 0)} creados → {fmtN(embudo[embudo.length - 1]?.value || 0)} finalizados</span> : null}
          info={{
            titulo: "Embudo operativo",
            descripcion: "Proyectos creados en el periodo que alcanzaron al menos cada hito del ciclo, con el % de conversión entre etapas. Cada etapa cuenta los proyectos con su fecha o la de cualquier etapa posterior (los sellos intermedios no siempre se rellenan en el ERP).",
            origen: "BD erpvisados_2023 — tabla proyectos (campos de fecha por estado).",
            formula: "Por etapa: COUNT(*) con la fecha de esa etapa o de alguna posterior NO NULA, sobre proyectos con fecha_alta en el rango.",
          }}
        >
          {embudo.length ? (
            <Funnel data={embudo} />
          ) : (
            <Empty icon={I.Funnel} title="Sin datos de embudo" />
          )}
        </ChartCard>

        <ChartCard
          icon={I.Clock} title="Antigüedad de proyectos activos"
          meta={aging?.total ? <span>{fmtN(aging.total)} activos · foto actual</span> : null}
          info={{
            titulo: "Antigüedad de proyectos activos",
            descripcion: "Distribución de los proyectos activos según los días transcurridos desde su creación. Foto actual: no depende del rango seleccionado. Detecta acumulación de backlog antiguo.",
            origen: "BD erpvisados_2023 — tabla proyectos.",
            formula: "Activos = estadosdeproyecto_id NOT IN (5, 6, 9). Tramos por DATEDIFF(hoy, fecha_alta).",
          }}
        >
          {aging?.total ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 6 }}>
              {aging.buckets.map((b, i) => (
                <div key={i}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
                    <span>{b.label}</span>
                    <span className="num">{fmtN(b.value)} · {fmtPct(b.pct)}</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: "var(--ce-border-subtle)", overflow: "hidden" }}>
                    <div style={{ width: `${b.pct}%`, height: "100%", background: agingColors[i] }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Empty icon={I.Clock} title="Sin proyectos activos" />
          )}
        </ChartCard>
      </div>

      {tareasOp ? (
        <div className="grid gc-4" style={{ marginTop: 20 }}>
          <KPI tier="secondary" tone="success" icon={I.CheckCheck}
            label="Tareas Realizadas"
            value={fmtN(tareasOp.realizadas)}
            extras={<span className="kpi-ref">de {fmtN(tareasOp.total)} · {fmtPct(tareasOp.pct_realizadas)}</span>}
            info={{
              descripcion: "Tareas CRM (leadtasks) creadas en el periodo y marcadas como realizadas.",
              origen: "BD erpvisados_2023 — leadtasks JOIN leads (empresa).",
              formula: "realizadas = COUNT(status = 1); % sobre el total creado en el rango.",
            }}
          />
          <KPI tier="secondary" tone="primary" icon={I.Percent}
            label="Puntualidad"
            value={fmtPct(tareasOp.pct_a_tiempo)}
            extras={<span className="kpi-ref">{fmtN(tareasOp.a_tiempo)} a tiempo · {fmtN(tareasOp.tarde)} tarde</span>}
            info={{
              descripcion: "Porcentaje de tareas realizadas que se cerraron en o antes de su fecha límite.",
              origen: "BD erpvisados_2023 — leadtasks.",
              formula: "a_tiempo = COUNT(realization_date <= due_date) / realizadas × 100.",
            }}
          />
          <KPI tier="secondary" tone="warning" icon={I.ListTodo}
            label="Vencidas Abiertas"
            value={fmtN(tareasOp.vencidas_abiertas)}
            extras={<span className="kpi-ref">foto actual, fuera de plazo</span>}
            info={{
              descripcion: "Tareas sin realizar cuya fecha límite ya pasó (foto actual, no depende del rango).",
              origen: "BD erpvisados_2023 — leadtasks.",
              formula: "COUNT(status = 0 AND due_date < hoy).",
            }}
          />
          <KPI tier="secondary" tone="cyan" icon={I.ChartLine}
            label="Acciones"
            value={fmtN(tareasOp.acciones)}
            extras={<span className="kpi-ref">registradas en el periodo</span>}
            info={{
              descripcion: "Acciones de seguimiento comercial registradas en el rango (leadactions).",
              origen: "BD erpvisados_2023 — leadactions JOIN leads (empresa).",
              formula: "COUNT(*) WHERE DATE(created) BETWEEN from AND to.",
            }}
          />
        </div>
      ) : null}

      {actividad ? (
        <div className="grid gc-7-5" style={{ marginTop: 20 }}>
          <ChartCard
            icon={I.ChartLine} title="Actividad por fuente (SDR vs Comercial)"
            meta={<span>en el periodo{segSuffix}</span>}
            info={{
              titulo: "Actividad por fuente",
              descripcion: "Volumen de actividad del periodo separando el trabajo de SDR/CRM (seguimiento de leads) del de Comercial/ERP (gestión del proyecto). Permite ver dónde se concentra el esfuerzo.",
              origen: "BD erpvisados_2023 — SDR: leadtasks/leadactions vía leads. Comercial: tareas (por FechaTarea) y fileactions (por uploaded).",
              formula: "SDR tareas/acciones = COUNT leadtasks/leadactions en el rango. Comercial tareas/archivos = COUNT tareas/fileactions en el rango.",
            }}
          >
            <table className="tbl">
              <thead>
                <tr>
                  <th>Fuente</th>
                  <th>Origen</th>
                  <th className="right">Tareas</th>
                  <th className="right">Acciones / archivos</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>SDR · CRM</td>
                  <td><span className="mono-pill">leadtasks · leadactions</span></td>
                  <td className="right num">{fmtN(actividad.sdr.tareas)}</td>
                  <td className="right num">{fmtN(actividad.sdr.acciones)}</td>
                </tr>
                <tr>
                  <td>Comercial · ERP</td>
                  <td><span className="mono-pill">tareas · fileactions</span></td>
                  <td className="right num">{fmtN(actividad.comercial.tareas)}</td>
                  <td className="right num">{fmtN(actividad.comercial.archivos)}</td>
                </tr>
              </tbody>
            </table>
          </ChartCard>

          <ChartCard
            icon={I.ListTodo} title="Seguimientos automáticos"
            meta={<span>{fmtN(actividad.sdr.automaticas)} de {fmtN(actividad.sdr.tareas)} tareas SDR</span>}
            info={{
              titulo: "Seguimientos automáticos",
              descripcion: "Porcentaje de las tareas CRM del periodo que son recordatorios automáticos «Seguir propuesta…» generados por el sistema. Se asignan a personas pero NO reflejan trabajo manual: conviene descontarlos al leer cargas de trabajo.",
              origen: "BD erpvisados_2023 — leadtasks.description.",
              formula: "automáticas = COUNT(description LIKE '%seguir propuesta%') sobre las tareas del rango; % sobre el total.",
            }}
          >
            <div style={{ paddingTop: 6 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
                <span className="num" style={{ fontSize: 30, fontWeight: 700 }}>{fmtPct(pctAuto)}</span>
                <span style={{ fontSize: 13, color: "var(--ce-text-muted)" }}>de las tareas SDR son automáticas</span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: "var(--ce-border-subtle)", overflow: "hidden", marginBottom: 12 }}>
                <div style={{ width: `${Math.min(100, pctAuto)}%`, height: "100%", background: "var(--ce-chart-4)" }} />
              </div>
              <p style={{ fontSize: 12.5, color: "var(--ce-text-muted)", margin: 0, lineHeight: 1.5 }}>
                Recordatorios «Seguir propuesta…» creados por el ERP. Cuentan como tareas asignadas a personas,
                pero no son trabajo manual: descuéntalos al evaluar la carga real de cada persona.
              </p>
            </div>
          </ChartCard>
        </div>
      ) : null}

      <div className="grid" style={{ gridTemplateColumns: "1fr", marginTop: 20 }}>
        <ChartCard
          icon={I.Users} title="Ranking de Gestores"
          meta={ranking.length ? <span>Top {ranking.length} por proyectos del periodo</span> : null}
          info={{
            titulo: "Ranking de Gestores",
            descripcion: "Rendimiento por usuario asignado a los proyectos del periodo: volumen, aceptación, valor pendiente de facturar y tareas vencidas abiertas a su cargo (estas dos últimas son foto actual).",
            origen: "BD erpvisados_2023 — proyectos (usuario_id) + proyectolineas + leadtasks.",
            formula: "Proyectos = COUNT por usuario_id (rango). Tasa = aceptados/proyectos. Valor pendiente = Σ líneas en estados 7/8/11. Tareas vencidas = leadtasks abiertas y fuera de plazo del usuario.",
          }}
        >
          {ranking.length ? (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Gestor</th>
                  <th className="right">Proyectos</th>
                  <th className="right">Aceptados</th>
                  <th className="right">Tasa acept.</th>
                  <th className="right">Valor pendiente</th>
                  <th className="right">Tareas vencidas</th>
                </tr>
              </thead>
              <tbody>
                {ranking.map((r, i) => (
                  <tr key={r.usuario_id ?? i}>
                    <td>{r.nombre}</td>
                    <td className="right num">{fmtN(r.proyectos)}</td>
                    <td className="right num">{fmtN(r.aceptados)}</td>
                    <td className="right num">{fmtPct(r.tasa_aceptacion)}</td>
                    <td className="right num">{fmtEur(r.valor_pendiente)}</td>
                    <td className="right num">
                      {r.tareas_vencidas > 0 ? <Badge tone="neg">{fmtN(r.tareas_vencidas)}</Badge> : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty icon={I.Users} title="Sin datos de gestores" />
          )}
        </ChartCard>
      </div>

      {tareasOp?.por_usuario?.length ? (
        <div className="grid" style={{ gridTemplateColumns: "1fr", marginTop: 20 }}>
          <ChartCard
            icon={I.ListTodo} title="Productividad por Persona (tareas)"
            meta={<span>Top {tareasOp.por_usuario.length} por tareas realizadas</span>}
            info={{
              titulo: "Productividad por Persona",
              descripcion: "Carga y cumplimiento de tareas CRM por persona asignada en el periodo: total, realizadas, puntualidad y tareas vencidas abiertas.",
              origen: "BD erpvisados_2023 — leadtasks (user_id) JOIN leads JOIN usuarios.",
              formula: "Agrupado por user_id sobre tareas creadas en el rango; % a tiempo = realizadas dentro de plazo / realizadas.",
            }}
          >
            <table className="tbl">
              <thead>
                <tr>
                  <th>Persona</th>
                  <th className="right">Tareas</th>
                  <th className="right">Realizadas</th>
                  <th className="right">% a tiempo</th>
                  <th className="right">Vencidas</th>
                </tr>
              </thead>
              <tbody>
                {tareasOp.por_usuario.map((r, i) => (
                  <tr key={i}>
                    <td>{r.nombre}</td>
                    <td className="right num">{fmtN(r.total)}</td>
                    <td className="right num">{fmtN(r.realizadas)}</td>
                    <td className="right num">{fmtPct(r.pct_a_tiempo)}</td>
                    <td className="right num">
                      {r.vencidas > 0 ? <Badge tone="neg">{fmtN(r.vencidas)}</Badge> : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ChartCard>
        </div>
      ) : null}
      </>
      )}

      <Footer />
    </>
  );
};

export default DashboardProyectos;
